import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PublicShell, { PublicCard, PublicError, PublicPasswordGate } from '../components/PublicShell';
import { downloadTransferFile, fetchPublicTransfer, isPasswordError, PublicApiError } from '../services/publicApi';
import type { PublicTransfer } from '../types';

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const glyphFor = (mime: string): string => {
  if (mime.startsWith('image/')) return '🖼️';
  if (mime.startsWith('video/')) return '🎬';
  if (mime.startsWith('audio/')) return '🎵';
  if (mime.includes('pdf')) return '📕';
  if (mime.includes('zip') || mime.includes('tar') || mime.includes('rar')) return '🗜️';
  return '📄';
};

export default function PublicTransferView() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<'loading' | 'password' | 'ready' | 'error'>('loading');
  const [transfer, setTransfer] = useState<PublicTransfer | null>(null);
  const [password, setPassword] = useState('');
  // The password that actually unlocked the transfer — reused for each download.
  const [unlocked, setUnlocked] = useState<string | undefined>(undefined);
  const [checking, setChecking] = useState(false);
  const [pwError, setPwError] = useState('');
  const [error, setError] = useState('');
  const [busyFileId, setBusyFileId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState('');

  async function load(pw?: string): Promise<void> {
    if (!token) return;
    try {
      const data = await fetchPublicTransfer(token, pw);
      setTransfer(data);
      setUnlocked(pw);
      setPhase('ready');
    } catch (e) {
      if (isPasswordError(e)) {
        // A 401 on the very first (password-less) load just means one is needed.
        setPwError(pw ? 'That password is not correct.' : '');
        setPhase('password');
        return;
      }
      setError(e instanceof PublicApiError ? e.message : 'This link is invalid or has expired.');
      setPhase('error');
    }
  }

  useEffect(() => { void load(); }, [token]);

  async function submitPassword() {
    setChecking(true);
    setPwError('');
    await load(password);
    setChecking(false);
  }

  async function handleDownload(fileId: string, filename: string) {
    setBusyFileId(fileId);
    setDownloadError('');
    try {
      await downloadTransferFile(token!, fileId, filename, unlocked);
    } catch (e) {
      setDownloadError(e instanceof PublicApiError ? e.message : 'Download failed. Try again.');
    } finally {
      setBusyFileId(null);
    }
  }

  async function downloadAll() {
    if (!transfer) return;
    for (const f of transfer.files) {
      await handleDownload(f.file_id, f.filename ?? 'download');
    }
  }

  return (
    <PublicShell>
      {phase === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 200, height: 120, borderRadius: 12, background: '#e5e7eb' }} />
          <div style={{ width: 140, height: 18, borderRadius: 6, background: '#e5e7eb' }} />
        </div>
      )}

      {phase === 'error' && <PublicError title="Transfer unavailable" message={error} />}

      {phase === 'password' && (
        <PublicPasswordGate
          label="This transfer is password protected"
          value={password}
          onChange={setPassword}
          onSubmit={() => void submitPassword()}
          busy={checking}
          error={pwError}
        />
      )}

      {phase === 'ready' && transfer && (
        <PublicCard>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e', wordBreak: 'break-word' }}>{transfer.name}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              {transfer.files.length} file{transfer.files.length === 1 ? '' : 's'} · {formatBytes(Number(transfer.total_bytes))}
              {' · '}
              Expires {new Date(transfer.expires_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>

          {transfer.files.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 14, textAlign: 'center', padding: '20px 0' }}>
              The files in this transfer are no longer available.
            </div>
          ) : (
            <>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                {transfer.files.map((f, i) => (
                  <div
                    key={f.file_id}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderTop: i === 0 ? 'none' : '1px solid #e5e7eb' }}
                  >
                    <span style={{ fontSize: 20 }}>{glyphFor(f.mime_type)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.filename ?? 'Untitled'}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{formatBytes(Number(f.size_bytes))}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleDownload(f.file_id, f.filename ?? 'download')}
                      disabled={busyFileId === f.file_id}
                      style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #d1d5db', background: 'white', fontSize: 13, fontWeight: 600, color: '#1a1a2e', cursor: busyFileId === f.file_id ? 'default' : 'pointer' }}
                    >
                      {busyFileId === f.file_id ? 'Downloading…' : 'Download'}
                    </button>
                  </div>
                ))}
              </div>

              {downloadError && (
                <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{downloadError}</div>
              )}

              {transfer.files.length > 1 && (
                <button
                  type="button"
                  onClick={() => void downloadAll()}
                  disabled={busyFileId !== null}
                  style={{ width: '100%', padding: '11px 12px', borderRadius: 8, border: 'none', background: busyFileId ? '#9ca3af' : '#2d5cf6', color: 'white', fontSize: 14, fontWeight: 600, cursor: busyFileId ? 'default' : 'pointer' }}
                >
                  Download all ({transfer.files.length})
                </button>
              )}
            </>
          )}
        </PublicCard>
      )}
    </PublicShell>
  );
}
